import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API } from "@/utils/api";
import { toast } from "sonner";
import {
  MapPin, Plus, Upload, RefreshCw, Search, X, Edit2, Trash2, Loader2, AlertCircle, CheckCircle2,
} from "lucide-react";

const SOURCE_BADGE = {
  MANUAL: "bg-slate-800 text-white border-slate-800",
  EXCEL_UPLOAD: "bg-blue-100 text-blue-700 border-blue-200",
  DELHIVERY_API: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function OdaPincodeManagement() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [networkType, setNetworkType] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [page] = useState(1);

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);

  const [check, setCheck] = useState("");
  const [checkResult, setCheckResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (networkType !== "ALL") params.set("network_type", networkType);
      if (search) params.set("search", search);
      params.set("page", page);
      params.set("limit", 100);
      const r = await api.get(`/v2/oda/pincodes?${params.toString()}`);
      let list = r.data?.items || [];
      if (sourceFilter !== "ALL") list = list.filter((i) => i.source === sourceFilter);
      setItems(list);
      setTotal(r.data?.total ?? list.length);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [networkType, search, sourceFilter, page]);

  useEffect(() => { load(); }, [load]);

  const stats = {
    total: items.length,
    own: items.filter((i) => i.network_type === "OWN").length,
    delhivery: items.filter((i) => i.network_type === "DELHIVERY").length,
    manual: items.filter((i) => i.is_manual_override).length,
  };

  const submitAdd = async (form) => {
    try {
      if (editing) {
        await api.put(`/v2/oda/pincodes/${editing.pincode}`, form);
        toast.success("Updated");
      } else {
        await api.post(`/v2/oda/pincodes`, form);
        toast.success("Pincode added");
      }
      setAddOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const delPin = async (pin) => {
    if (!window.confirm(`Deactivate pincode ${pin}?`)) return;
    try {
      await api.delete(`/v2/oda/pincodes/${pin}`);
      toast.success("Deactivated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/oda/upload/excel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Upload failed");
      toast.success(`${data.uploaded} uploaded, ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const syncDelhivery = async () => {
    setSyncBusy(true);
    try {
      const r = await api.post(`/v2/oda/sync/delhivery`);
      toast.success(`Synced ${r.data.synced_count}, ${r.data.skipped_manual_count} manual overrides preserved`);
      setSyncOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSyncBusy(false); }
  };

  const runCheck = async () => {
    if (!check) return;
    try {
      const r = await api.get(`/v2/oda/check/${check}`);
      setCheckResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid pincode");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="oda-management-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">ODA Pincode Management</h1>
            <p className="text-sm text-slate-500">Out of Delivery Area configuration with surcharges</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => uploadExcel(e.target.files?.[0])}
                data-testid="excel-upload-input"
              />
              <span className="inline-flex items-center px-3 h-10 rounded border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:border-slate-400">
                <Upload className="w-4 h-4 mr-1" /> Upload Excel
              </span>
            </label>
            <Button variant="outline" onClick={() => setSyncOpen(true)} data-testid="sync-delhivery-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Sync Delhivery
            </Button>
            <Button onClick={() => { setEditing(null); setAddOpen(true); }} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="add-pincode-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Pincode
            </Button>
          </div>
        </div>

        {/* Quick check widget */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-sm text-slate-700 flex items-center gap-1.5">
            <Search className="w-4 h-4 text-slate-400" /> Quick check:
          </div>
          <Input
            value={check}
            onChange={(e) => setCheck(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Enter pincode"
            className="w-40"
            data-testid="check-pincode-input"
          />
          <Button onClick={runCheck} variant="outline" data-testid="check-pincode-btn">Check</Button>
          {checkResult && (
            <div className={`text-sm font-semibold flex items-center gap-1 ${
              checkResult.is_oda ? "text-amber-700" : "text-emerald-700"
            }`} data-testid="check-result">
              {checkResult.is_oda ? (
                <>
                  <AlertCircle className="w-4 h-4" /> ODA ✅ — {checkResult.surcharge_type} ₹{checkResult.surcharge_amount}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Not ODA ❌ (regular delivery)
                </>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total ODA" value={total} />
          <Stat label="Own Network" value={stats.own} color="emerald" />
          <Stat label="Delhivery" value={stats.delhivery} color="blue" />
          <Stat label="Manual Overrides" value={stats.manual} color="amber" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex gap-2 flex-wrap">
          <select value={networkType} onChange={(e) => setNetworkType(e.target.value)} className="border border-slate-300 rounded h-10 px-2 text-sm bg-white" data-testid="filter-network">
            <option value="ALL">All networks</option>
            <option value="OWN">Own</option>
            <option value="DELHIVERY">Delhivery</option>
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="border border-slate-300 rounded h-10 px-2 text-sm bg-white" data-testid="filter-source">
            <option value="ALL">All sources</option>
            <option value="MANUAL">Manual</option>
            <option value="EXCEL_UPLOAD">Excel</option>
            <option value="DELHIVERY_API">Delhivery API</option>
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search pincode" className="pl-8" data-testid="filter-search" />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-slate-400"><MapPin className="w-10 h-10 mx-auto mb-2 opacity-60" /><p className="text-sm">No ODA pincodes</p></div>
          ) : (
            <table className="w-full text-sm" data-testid="oda-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-3">Pincode</th>
                  <th className="text-left p-3">Network</th>
                  <th className="text-left p-3">Surcharge Type</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-center p-3">Source</th>
                  <th className="text-center p-3">Override</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((i) => (
                  <tr key={i.pincode + i.network_type} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-sm font-semibold">{i.pincode}</td>
                    <td className="p-3">{i.network_type}</td>
                    <td className="p-3">{i.surcharge_type}</td>
                    <td className="p-3 text-right font-semibold">₹{Number(i.surcharge_amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${SOURCE_BADGE[i.source] || SOURCE_BADGE.MANUAL}`}>
                        {i.source || "—"}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {i.is_manual_override ? (
                        <span className="text-[10px] font-bold px-2 py-1 rounded border bg-amber-100 text-amber-700 border-amber-200">
                          PROTECTED
                        </span>
                      ) : "—"}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => { setEditing(i); setAddOpen(true); }} className="p-1.5 text-slate-500 hover:text-slate-800 rounded" data-testid={`edit-${i.pincode}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => delPin(i.pincode)} className="p-1.5 text-slate-500 hover:text-red-700 rounded" data-testid={`delete-${i.pincode}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {addOpen && (
        <AddEditModal
          editing={editing}
          onClose={() => { setAddOpen(false); setEditing(null); }}
          onSubmit={submitAdd}
        />
      )}
      {syncOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={() => setSyncOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-md shadow-2xl" data-testid="sync-modal">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Sync from Delhivery</h3>
            <p className="text-sm text-slate-600">
              This will update all non-manual ODA pincodes from Delhivery&apos;s ODA list. Manual overrides are protected and will not be touched.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setSyncOpen(false)}>Cancel</Button>
              <Button onClick={syncDelhivery} disabled={syncBusy} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="confirm-sync-btn">
                {syncBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Sync Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, color = "slate" }) {
  const c = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    slate: "bg-white border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-xl p-3 ${c}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function AddEditModal({ editing, onClose, onSubmit }) {
  const [form, setForm] = useState(
    editing
      ? {
          pincode: editing.pincode,
          network_type: editing.network_type || "ALL",
          surcharge_type: editing.surcharge_type || "FLAT",
          surcharge_amount: editing.surcharge_amount ?? "",
        }
      : { pincode: "", network_type: "ALL", surcharge_type: "FLAT", surcharge_amount: "" }
  );
  const submit = () => {
    if (!form.pincode || form.surcharge_amount === "") {
      toast.error("Pincode and amount are required");
      return;
    }
    onSubmit({
      ...form,
      surcharge_amount: Number(form.surcharge_amount),
    });
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-md shadow-2xl" data-testid="oda-add-modal">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg font-bold text-slate-800">{editing ? "Edit Pincode" : "Add ODA Pincode"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Pincode*</label>
            <Input
              maxLength={6}
              value={form.pincode}
              onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })}
              disabled={!!editing}
              data-testid="modal-pincode"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Network</label>
            <select value={form.network_type} onChange={(e) => setForm({ ...form, network_type: e.target.value })} className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white" data-testid="modal-network">
              <option value="ALL">ALL</option>
              <option value="OWN">OWN</option>
              <option value="DELHIVERY">DELHIVERY</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Surcharge Type</label>
            <select value={form.surcharge_type} onChange={(e) => setForm({ ...form, surcharge_type: e.target.value })} className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white" data-testid="modal-surcharge-type">
              <option value="FLAT">FLAT</option>
              <option value="PER_KG">PER_KG</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-600 mb-1">Surcharge Amount (₹)*</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.surcharge_amount}
              onChange={(e) => setForm({ ...form, surcharge_amount: e.target.value })}
              data-testid="modal-amount"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="modal-submit">
            {editing ? "Update" : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
}
